/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Waiting } from '@msdyn365-commerce-modules/utilities';

// const isBrowser = typeof window !== 'undefined';

export interface ILdsAccountViewProps {
    slots: {
        accountInformation: {
            key: string;
            [key: string]: any;
        }[];
    };
    selectedOption?: string;
    onSelectionChange?: (value: string) => void;
    resources: any;
}

export default (props: ILdsAccountViewProps) => {
    // Define the type for menu items
    interface MenuItem {
        type: string;
        text: string;
        value?: string;
        accountNum?: string;
    }

    const menuItems: MenuItem[] = [
        { type: 'header', text: props.resources.storeAccount },
        {
            type: 'option',
            text: props.resources.orderHistory,
            value:
                props.slots.accountInformation.find(item => item.key.includes('lds-customer-order-history'))?.key ||
                'lds-account__0-lds-customer-order-history'
        },
        {
            type: 'option',
            text: props.resources.addresses,
            value:
                props.slots.accountInformation.find(item => item.key.includes('lds-account-management-address'))?.key ||
                'lds-account__0-lds-account-management-address'
        },
        { type: 'header', text: props.resources.churchAccount },
        {
            type: 'option',
            text: props.resources.profile,
            value:
                props.slots.accountInformation.find(item => item.key.includes('account-profile'))?.key || 'lds-account__0-account-profile'
        }
    ];
    console.log(props.slots.accountInformation);

    const [internalSelectedOption, setInternalSelectedOption] = React.useState<string>(() => {
        const customerOrderHistoryKey = props.slots.accountInformation.find(item => item.key.includes('lds-customer-order-history'))?.key;
        return props.selectedOption ?? customerOrderHistoryKey ?? 'lds-account__0-lds-customer-order-history';
    });
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    // @ts-ignore
    const selectList = props.data.paymentTypes.result?.filter((result: any) => {
        return result.ACCOUNTNUM !== '';
    });

    // Update internal state when prop changes
    React.useEffect(() => {
        if (props.selectedOption) {
            setInternalSelectedOption(props.selectedOption);
        }
    }, [props.selectedOption]);

    // Check URL parameters on component mount and update internalSelectedOption
    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const section = urlParams.get('section');

            if (section) {
                let matchingValue: string | undefined;

                // Map URL section names to their corresponding values
                switch (section) {
                    case 'unit-order-history':
                        matchingValue =
                            props.slots.accountInformation.find(item => item.key.includes('lds-order-history'))?.key ||
                            'lds-account__0-lds-order-history';
                        break;
                    case 'customer-order-history':
                        matchingValue =
                            props.slots.accountInformation.find(item => item.key.includes('lds-customer-order-history'))?.key ||
                            'lds-account__0-lds-customer-order-history';
                        break;
                    default:
                        // For other sections, find the menu item that matches
                        matchingValue = menuItems.find(
                            item => item.type === 'option' && item.value && item.value.toLowerCase().includes(section.toLowerCase())
                        )?.value;
                }

                if (matchingValue) {
                    setInternalSelectedOption(matchingValue);
                } else {
                    // If no valid section is found, default to customer order history
                    const customerOrderHistoryKey = props.slots.accountInformation.find(item =>
                        item.key.includes('lds-customer-order-history')
                    )?.key;
                    setInternalSelectedOption(customerOrderHistoryKey ?? 'lds-account__0-lds-customer-order-history');
                }
            } else {
                // If no section parameter exists, default to customer order history
                const customerOrderHistoryKey = props.slots.accountInformation.find(item => item.key.includes('lds-customer-order-history'))
                    ?.key;
                setInternalSelectedOption(customerOrderHistoryKey ?? 'lds-account__0-lds-customer-order-history');
            }

            // Set loading to false after processing URL parameters
            setIsLoading(false);
        } else {
            // If not in browser, set loading to false immediately
            setIsLoading(false);
        }
    }, []);

    // Function to update URL with the selected section
    const updateUrlWithSection = (value: string) => {
        if (typeof window !== 'undefined') {
            const selectedItem = menuItems.find(item => item.value === value);
            if (selectedItem && selectedItem.type === 'option') {
                let sectionName: string;

                // Handle different types of order history
                const orderHistoryKey = props.slots.accountInformation.find(item => item.key.includes('lds-order-history'))?.key;
                console.log('orderHistoryKey', orderHistoryKey);
                const customerOrderHistoryKey = props.slots.accountInformation.find(item => item.key.includes('lds-customer-order-history'))
                    ?.key;

                if (value === (orderHistoryKey || 'lds-account__0-lds-order-history')) {
                    sectionName = 'unit-order-history';
                } else if (value === (customerOrderHistoryKey || 'lds-account__0-lds-customer-order-history')) {
                    sectionName = 'customer-order-history';
                } else {
                    // For other sections, use the last part of the value
                    const parts = value.split('-');
                    sectionName = parts[parts.length - 1];
                }

                // Update URL without reloading the page
                const url = new URL(window.location.href);
                url.searchParams.set('section', sectionName);
                window.history.pushState({}, '', url.toString());
            }
        }
    };

    const handleSelectionChange = (value: string | any) => {
        setInternalSelectedOption(value);

        // Update URL with the selected section
        updateUrlWithSection(value);

        if (props.onSelectionChange) {
            // Find the selected menu item to get the account number if it's a unit order history option
            const selectedItem = menuItems.find(item => item.value === value);
            const orderHistoryKey = props.slots.accountInformation.find(item => item.key.includes('lds-order-history'))?.key;
            const isOrderHistory = value === (orderHistoryKey || 'lds-account__0-lds-order-history');

            if (selectedItem && isOrderHistory && 'accountNum' in selectedItem) {
                // Pass both the value and the account number
                props.onSelectionChange(`${value}|${selectedItem.accountNum}`);

                // Navigate to the order history view with the account parameter
                const accountNum = selectedItem.accountNum;
                if (accountNum) {
                    // Use window.location to navigate to the order history view with the account parameter
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set('account', accountNum);
                    window.location.href = currentUrl.toString();
                }
            } else {
                props.onSelectionChange(value);
            }
        }
    };

    if (selectList && selectList.length > 0) {
        const unitOrderHistoryItems: any[] = [{ type: 'header', text: 'Unit Order History' }];
        const orderHistoryKey = props.slots.accountInformation.find(slot => slot.key.includes('lds-order-history'))?.key;

        selectList.forEach((item: any) => {
            // Include the account number in the value to pass to lds-order-history
            unitOrderHistoryItems.push({
                type: 'option',
                text: item.PAYMENTMETHOD,
                value: orderHistoryKey || 'lds-account__0-lds-order-history',
                accountNum: item.ACCOUNTNUM // Store the account number for reference
            });
        });
        menuItems.splice(3, 0, ...unitOrderHistoryItems);
    }

    // Show loading state while initializing
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', marginBottom: '30px' }}>
                <Waiting />
            </div>
        );
    }

    return (
        <div className='lds-account-container'>
            <div className='lds-account-navigation-container'>
                <select
                    value={internalSelectedOption}
                    onChange={e => handleSelectionChange(e.target.value)}
                    className='lds-account-mobile-select'
                >
                    {menuItems.map((item, index) => {
                        if (item.type === 'header') {
                            const nextHeaderIndex = menuItems.findIndex((nextItem, i) => i > index && nextItem.type === 'header');
                            const options = menuItems
                                .slice(index + 1, nextHeaderIndex === -1 ? undefined : nextHeaderIndex)
                                .filter(option => option.type === 'option');

                            return options.length > 0 ? (
                                <optgroup key={index} label={item.text} className='lds-account-menu-item-header'>
                                    {options.map((option, optIndex) => (
                                        <option key={`${index}-${optIndex}`} value={option.value} className='lds-account-menu-item'>
                                            {option.text}
                                        </option>
                                    ))}
                                </optgroup>
                            ) : null;
                        }
                        return null;
                    })}
                </select>
                <ol className='lds-account-menu-list'>
                    {menuItems.map((item, index) =>
                        item.type === 'header' ? (
                            <li key={index} className='lds-account-menu-item-header'>
                                {item.text}
                            </li>
                        ) : (
                            <li key={index} className='lds-account-menu-item'>
                                <a
                                    onClick={e => {
                                        e.preventDefault();
                                        handleSelectionChange(item.value);
                                    }}
                                    className={`lds-account-menu-link ${
                                        internalSelectedOption === item.value ? 'lds-account-menu-link-active' : ''
                                    }`}
                                >
                                    {item.text}
                                </a>
                            </li>
                        )
                    )}
                </ol>
            </div>
            <div className='lds-account-content-container'>
                {/* @ts-ignore */}
                {props.slots.accountInformation.map((item, index) => {
                    // @ts-ignore
                    if (item.key === internalSelectedOption) {
                        return <div key={index}>{item}</div>;
                    }
                })}
            </div>
        </div>
    );
};
